import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Text, View, StyleSheet, TouchableWithoutFeedback, ActivityIndicator  } from "react-native";
import { Slider } from '@miblanchard/react-native-slider';
import { FontAwesome } from '@expo/vector-icons';

import { types } from "redux/types";
import { dropShadow } from "styles/shared";
import { getLangTerm } from "lang";
import { screenContainer } from "../styles/shared";

const playerStyles = StyleSheet.create({
    container: {
        ...dropShadow(0, -2, 4, .25),
        height: 80,
        paddingVertical: 20,
        backgroundColor: "#fff",
        justifyContent: "center"
    },
    contentContainer: {
        ...screenContainer,
        paddingHorizontal: 28,
        flexDirection: "row",
        alignItems: "center"
    },
    button: {
        width: 24
    },
    slider: {
        flex: 1,
        marginHorizontal: 10
    },
    track: {
        height: 4,
        borderRadius: 4,
        borderWidth: 4,
        borderColor: "#30C0D3"
    },
    thumb: {
        width: 24,
        height: 24,
        borderRadius: 20,
        borderWidth: 5,
        borderColor: "#30C0D3",
        backgroundColor: "#FFF",
    },
    loading: {
        marginRight: 20
    }
});

function millisToMinutesAndSeconds(millis) {
    var minutes = Math.floor(millis / 60000);
    var seconds = ((millis % 60000) / 1000).toFixed(0);
    return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
}

const AudioPlayer = ({slug, routeSlug, audioURL, color, timestamp}) => {

    if (!audioURL) {
        return null;
    }

    const lang = useSelector(state => state.settings.lang);
    const dispatch = useDispatch();

    const audioRef = useRef(null);
    const isMounted = useRef(true);

    const [isPlaying, setIsPlaying] = useState(false);
    const [wasPlaying, setWasPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [duration, setDuration] = useState(null);
    const [position, setPosition] = useState(0);
    const [positionStr, setPositionStr] = useState("0:00");

    // Initialize audio element
    useEffect(() => {
        isMounted.current = true;

        // Create audio element using DOM API (more compatible with React Native Web)
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.style.display = 'none';

        // Attach to DOM to ensure proper event firing
        document.body.appendChild(audio);
        audioRef.current = audio;

        const audioUrl = `${audioURL}${timestamp ? `?t=${timestamp}` : ""}`;
        audio.src = audioUrl;

        // console.log('Created audio element and attached to DOM:', audio);

        // Event handlers
        const handleLoadedMetadata = () => {
            if (isMounted.current && !isNaN(audio.duration)) {
                const durMillis = audio.duration * 1000;
                setDuration(durMillis);
                setPositionStr(`0:00/${millisToMinutesAndSeconds(durMillis)}`);
                setIsLoading(false);
                // console.log('Audio loaded, duration:', durMillis);
            }
        };

        const handleTimeUpdate = () => {
            console.log('timeupdate fired, currentTime:', audio.currentTime, 'paused:', audio.paused);
            if (isMounted.current) {
                const posMillis = audio.currentTime * 1000;
                const durMillis = audio.duration * 1000;
                setPosition(posMillis);
                if (!isNaN(durMillis)) {
                    setPositionStr(`${millisToMinutesAndSeconds(posMillis)}/${millisToMinutesAndSeconds(durMillis)}`);
                }
            }
        };

        const handleEnded = () => {
            if (isMounted.current) {
                setIsPlaying(false);
                setPosition(0);
                audio.currentTime = 0;
            }
        };

        const handleError = (e) => {
            console.error('Audio error:', audio.error);
            if (isMounted.current) {
                setIsLoading(false);
            }
        };

        const handleCanPlay = () => {
            // console.log('Audio can play');
        };

        const handlePlay = () => {
            // console.log('Audio play event');
            if (isMounted.current) {
                setIsPlaying(true);
            }
        };

        const handlePause = () => {
            // console.log('Audio pause event');
            if (isMounted.current) {
                setIsPlaying(false);
            }
        };

        // Attach event listeners
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);

        // Cleanup
        return () => {
            console.log('CLEANUP: AudioPlayer useEffect cleanup called!');
            isMounted.current = false;
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.pause();
            audio.src = '';
            // Remove from DOM
            if (audio.parentNode) {
                audio.parentNode.removeChild(audio);
            }
        };
    }, [audioURL, timestamp]);

    const togglePlaying = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            console.log('Pausing audio');
            audio.pause();
            dispatch({
                type: types.PLAY_AUDIO,
                userActionData: { slug, audioURL, positionStr, stop: true }
            });
        } else {
            console.log('Playing audio');
            try {
                const playPromise = audio.play();
                console.log('After calling play(), audio state:', {
                    paused: audio.paused,
                    currentTime: audio.currentTime,
                    duration: audio.duration,
                    volume: audio.volume,
                    muted: audio.muted,
                    readyState: audio.readyState,
                    src: audio.src
                });
                await playPromise;
                console.log('Play promise resolved, audio state:', {
                    paused: audio.paused,
                    currentTime: audio.currentTime
                });
                dispatch({
                    type: types.PLAY_AUDIO,
                    userActionData: { slug, audioURL, positionStr }
                });
            } catch (e) {
                console.error('Error playing audio:', e);
            }
        }
    };

    const handleSlideStart = () => {
        setWasPlaying(isPlaying);
        if (isPlaying && audioRef.current) {
            audioRef.current.pause();
        }
    };

    const handleSlideComplete = (values) => {
        const audio = audioRef.current;
        if (!audio) return;

        const time = Math.round(values[0]);
        audio.currentTime = time / 1000; // Convert ms to seconds
        setPosition(time);

        if (wasPlaying) {
            audio.play();
        }
        setWasPlaying(false);
    };

    return (
        <View style={playerStyles.container}>
            <View style={playerStyles.contentContainer}>
                {isLoading && (
                    <>
                        <ActivityIndicator size="small" color="#287773" style={playerStyles.loading} />
                        <Text>{getLangTerm("audio-loading", lang)}</Text>
                    </>
                )}
                {!isLoading && (
                    <>
                        <View style={playerStyles.button}>
                            <TouchableWithoutFeedback onPress={togglePlaying}>
                                <FontAwesome
                                    name={isPlaying ? "pause" : "play"}
                                    size={26}
                                    style={{color: color}}
                                />
                            </TouchableWithoutFeedback>
                        </View>
                        <View style={playerStyles.slider}>
                            <Slider
                                maximumValue={Math.max(duration || 0, 1, position + 1)}
                                onSlidingStart={handleSlideStart}
                                onSlidingComplete={handleSlideComplete}
                                value={position || 0}
                                thumbStyle={playerStyles.thumb}
                                trackStyle={playerStyles.track}
                            />
                        </View>
                        <Text>{positionStr}</Text>
                    </>
                )}
            </View>
        </View>
    );
};

export default AudioPlayer;
